import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import type { Service } from 'webapp-types';
import { BillingCycle } from 'webapp/enums/enums';
import { getIntervalTime } from 'webapp/utils/number';

import eq from 'webapp/helpers/eq';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import CommonUpgradePlanLinkComponent from 'webapp/components/common/upgrade-plan-link';
import ToolTipWithIconComponent from 'webapp/components/tool-tip-with-icon';

export default class BillingSubscriptionInfoComponent extends Component {
	@service('billing') declare billingService: Service.Billing;
	@service('flow/annual-plan') declare flowAnnualPlanService: Service.Flow.AnnualPlan;

	get currentStatus() {
		const activeUntilDate = this.billingService.formattedActiveUntil,
			nextPaymentDate = this.billingService.formattedNextPaymentDate,
			activePlan = this.billingService.activePlan;
		let text, type, icon: IconName | undefined;

		if (this.billingService.isExpiredTrial) {
			text = `Your free trial has expired on ${activeUntilDate}. Please select a plan`;
			type = 'expired';
			icon = 'Alarm';
		} else if (this.billingService.isTrial) {
			if (this.billingService.hasPayment) {
				text = `Your subscription is scheduled to start on ${activeUntilDate}, once your trial ends.`;
			} else {
				text = `You are on a free trial until ${activeUntilDate}.`;
			}
			type = 'trial';
			icon = 'Hourglass';
		} else if (this.billingService.isPaused) {
			text = `Paused until ${activeUntilDate}. Data will not update until you re-activate.`;
			type = 'error';
			icon = 'AlertFill';
		} else if (this.billingService.isExpired) {
			text = `Expired on ${activeUntilDate}. Data will not update until you re-activate.`;
			type = 'expired';
			icon = 'Alarm';
		} else if (this.billingService.isCanceled) {
			text = 'Scheduled to be canceled ';
			if (
				this.billingService.activeTimeLeft > 0 &&
				this.billingService.activeTimeLeft < 24 * 60 * 60
			) {
				// less than a day
				const hours = this.billingService.activeTimeLeft / (60 * 60);
				text += `in ${getIntervalTime(hours, 'hours', true)}.`;
			} else {
				text += `on ${activeUntilDate}.`;
			}
			type = 'expired';
			icon = 'Alarm';
		} else if (this.billingService.isPaymentError) {
			const formattedFailureMessage = this.billingService.formattedFailureMessage
				? ` (${this.billingService.formattedFailureMessage})`
				: '';
			text = `Your latest payment failed${formattedFailureMessage}. Please update payment information before ${activeUntilDate} to avoid any interruption of services.`;
			type = 'error';
			icon = 'AlertFill';
		} else if (activePlan?.isZeroPayment) {
			text = '';
			type = 'active';
			icon = 'CheckCircleFill';
		} else if (this.billingService.isActive) {
			text = `Active and auto-renews on ${nextPaymentDate}.`;
			type = 'active';
			icon = 'CheckCircleFill';
		}

		return { text, type, icon };
	}

	get currentPlanTypeClass() {
		const { isPaused, isPaymentError, isExpired, isCanceled, activePlan } = this.billingService;

		let className = 'subscription-info__header--';
		if (isPaused || isPaymentError || isExpired || isCanceled) {
			return null;
		} else if (activePlan?.isFree) {
			className += `free${activePlan?.isLegacy ? '-legacy' : ''}`;
		} else {
			className += activePlan?.planType || '';
		}

		return className;
	}

	get showLegacyLabel() {
		return this.billingService.isActiveAny && this.billingService.activePlan?.isLegacy;
	}

	get invoiceItems() {
		return this.billingService.invoiceItems;
	}

	get invoiceTotal() {
		const upcomingInvoiceData = this.billingService.upcomingInvoiceData;
		return {
			billingCycle: upcomingInvoiceData?.billingCycle,
			totalMonthlyFormatted: upcomingInvoiceData?.totalMonthlyFormatted,
			totalFormatted: upcomingInvoiceData?.totalFormatted,
		};
	}

	get showBilledCycleTotal() {
		return this.invoiceTotal.billingCycle !== BillingCycle.MONTHLY;
	}

	get hasExtraDS() {
		return (
			this.billingService.activePlan?.freeSourcesFormatted !==
			this.billingService.activePlan?.freeSources
		);
	}

	get showAnnualBanner() {
		return this.flowAnnualPlanService.isEligibleToShowYearlyNotification;
	}

	get submitInProgress() {
		return this.billingService.requestTrialInProgress;
	}

	@action
	editPaymentInfo() {
		this.billingService.changePayment();
	}

	@action
	changeBillingPeriodToYearly() {
		const currentPlanType = this.billingService.activePlan?.planType,
			targetPlan = this.billingService.availablePlans
				.filter((item) => item.planType === currentPlanType)
				.find((item) => item.isYearly);

		if (this.billingService.isActiveTrial) {
			void this.billingService.requestTrial(null, targetPlan, 'billing_page');
		} else if (targetPlan) {
			this.billingService.switchPlan(targetPlan, {
				billingPeriodChangePopup: 'billing_page_cta',
			});
		}
	}
	<template>
		<div class="subscription-info">
			<div
				class="subscription-info__header subscription-info__header--{{this.currentStatus.type}}
					{{if this.currentPlanTypeClass this.currentPlanTypeClass}}"
			>
				<div class="topline">
					<span>Your subscription</span>
					{{#if this.billingService.isCanceled}}
						<a {{on "click" this.editPaymentInfo}}>Reactivate</a>
					{{else}}
						<CommonUpgradePlanLinkComponent
							@text="Change plan"
							@trackingOrigin="billing_page"
						/>
					{{/if}}
				</div>
				<div class="title">
					{{#if this.currentStatus.icon}}
						<CommonUiIconComponent
							@name={{this.currentStatus.icon}}
							@size="2xl"
							@class="icon"
						/>
					{{/if}}
					<h1>
						{{this.billingService.activePlan.name}}
						{{#if this.showLegacyLabel}}
							- Legacy
						{{/if}}
					</h1>
					{{#if this.currentStatus.text}}
						<span>{{this.currentStatus.text}}</span>
					{{/if}}
				</div>
			</div>
			{{#if this.invoiceItems}}
				<div class="subscription-info__content">
					<div class="subscription-info__list">
						{{#each this.invoiceItems as |item|}}
							<div class={{item.type}}>
								<div>
									<strong>
										{{#if item.shownQuantity}}
											{{item.shownQuantity}}
										{{/if}}
										{{item.name}}
									</strong>
									{{#if item.description}}
										{{item.description}}
									{{/if}}
									{{#unless this.billingService.is2026SeptBillingGroup}}
										{{#if (eq item.type "plan")}}
											({{this.billingService.activePlan.freeSourcesFormatted}}
											Data sources included)
											{{! template-lint-disable simple-unless }}
											{{#if this.hasExtraDS}}
												<ToolTipWithIconComponent
													@text="You have extra data source(s) at no charge."
													@iconName="Question"
													@maxWidth={{230}}
												/>
											{{/if}}
										{{/if}}
									{{/unless}}
									{{#if item.priceOff}}
										<span class="price-off">{{item.priceOff}}</span>
									{{/if}}
								</div>
								<div><strong>{{item.formattedPrice}}</strong>/mo</div>
							</div>
						{{/each}}
					</div>
					<div class="subscription-info__summary">
						<div class="sum sum--total">
							<div>Total:</div>
							<div>
								<strong>{{this.invoiceTotal.totalMonthlyFormatted}}</strong>
								/mo
							</div>
						</div>
						{{#if this.showBilledCycleTotal}}
							<div class="sum sum--billing-cycle">
								<div>Billed {{this.invoiceTotal.billingCycle}}:</div>
								<div>
									<strong>{{this.invoiceTotal.totalFormatted}}</strong>
								</div>
							</div>
						{{/if}}
					</div>

					{{#if this.showAnnualBanner}}
						<div>
							<div class="upgrade">
								<CommonUiIconComponent @name="Percentage" @size="sm" />
								<div class="upgrade__text">
									<span>{{{this.billingService.yearlyDiscountText}}}</span>
								</div>
								<a role="button" {{on "click" this.changeBillingPeriodToYearly}}>
									Switch to annual
								</a>
							</div>
						</div>
					{{/if}}
				</div>
			{{/if}}
		</div>
	</template>
}
